<template>
  <div class="form-wrapper email-verification-form">
    <SmallLogo v-if="!isUpdateEmailPopup" />
    <div
      v-if="!isUpdateEmailPopup"
      class="onboarding_form_title"
    >
      {{ translate('checkEmail') }}
    </div>
    <div class="onboarding_form_subtitle">
      {{ translate('weSentALoginCode') }}
      <span class="send-code-to-email">{{ getEmail }}</span>
      <br />
      <ButtonCustom
        v-if="!isUpdateEmailPopup"
        class="resend-button"
        :text="translate('resendLoginCode')"
        :type="'popup-button-wrap'"
        @click="onSendLoginCode"
      />
    </div>

    <div class="input-wrap verification_code">
      <span class="login-bold">
        {{ translate('enterLoginCode') }}
      </span>
      <input
        ref="codeinput"
        v-model="inputs"
        maxlength="6"
        class="input-field"
        type="number"
        :placeholder="translate('sixDigits')"
        @input="verifyEmail"
      />
    </div>
    <ErrorMessage v-if="showErrorMessage" />
    <br />
    <div
      v-if="!isUpdateEmailPopup"
      class="info-footer"
    >
      <div class="footer-buttons">
        <div
          class="back-button text-text-default pt"
          @click="backStep"
        >
          <em
            class="custom-icon flex h-4 w-4 bg-icon-default"
            :style="getCustomIconStyle(iconPath('arrow-left'))"
          />
          {{ translate('back') }}
        </div>
        <ButtonCustom
          :text="translate('next')"
          :type="'popup-button-wrap'"
          :button-dimensions="{
            width: 65,
            height: 28,
          }"
          :disabled="true"
          :custom-icon-name="'arrow-right'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SmallLogo from '@/components/elements/SmallLogo';
import ErrorMessage from '@/components/elements/forms/ErrorMessage';
import loginMixin from '@/mixins/loginMixin';
import ButtonCustom from '@/components/elements/ButtonCustom';
import customIconsMixin from '@/mixins/customIconsMixin';
import { browserStorageGetItem } from '@/utilities/browser-storage.util';
import { loggingInDifferentEmailKey } from '@/types/login';

export default {
  name: 'VerifyEmailCodeForm',
  components: {
    SmallLogo,
    ErrorMessage,
    ButtonCustom,
  },
  mixins: [loginMixin, customIconsMixin],
  props: {
    newEmail: {
      type: String,
      default: '',
      required: false,
    },
  },
  emits: ['newEmailVerified'],
  data() {
    return {
      inputs: '',
      isLoginCodeResent: false,
    };
  },
  computed: {
    isUpdateEmailPopup() {
      return this.$store.getters.getShowUpdateEmailPopup;
    },
    // For loggin in
    defaultTabName() {
      return this.$store.getters.getDefaultTabName;
    },

    popupMessageNotEmpty() {
      return this.$store.getters.getPopUpMessage !== '';
    },
    getEmail() {
      return this.isUpdateEmailPopup
        ? this.newEmail
        : this.$store.getters.getEmail || browserStorageGetItem(loggingInDifferentEmailKey);
    },
    showErrorMessage() {
      return this.$store.getters.onboardingErrorMessage > '';
    },
    completeEmailCode() {
      return `${this.inputs}`;
    },
  },
  mounted() {
    this.resetInput();
  },
  created() {
    if (!this.isUpdateEmailPopup) {
      window.addEventListener('resize', this.scrollToBtm);
    }
  },
  unmounted() {
    if (!this.isUpdateEmailPopup) {
      window.removeEventListener('resize', this.scrollToBtm);
    }
  },

  methods: {
    backStep() {
      this.$router.push({
        path: '/login',
      });
    },
    displayUiNotification(message, status) {
      this.$store.dispatch('addUiNotification', {
        message: this.translate(message),
        status: this.$constants.uiNotificationStatuses[status],
      });
    },
    verifyEmail() {
      // Make sure that the code is completely entered
      if (this.completeEmailCode.length === 6) {
        // handle verifying when updating users email
        if (this.isUpdateEmailPopup && this.newEmail) {
          this.$store
            .dispatch('updateUserEmail', {
              email: this.newEmail,
              code: this.completeEmailCode,
            })
            .then(() => {
              this.$emit('newEmailVerified');
            })
            .catch((err) => {
              switch (err.response.data.message) {
                case 'email update verification code has expired':
                  this.displayUiNotification('loginCodeErrorExpired', 'error');
                  break;
                case 'too many attempts - please request a new email update verification code':
                  this.onSendLoginCode();
                  this.displayUiNotification('loginCodeErrorRateLimited', 'error');
                  break;
                case 'email update verification code is incorrect':
                  this.displayUiNotification('loginCodeError', 'error');
                  break;
              }
              this.resetInput();
            });
          return;
        }

        // handle verifying when logging in with email
        this.$store
          .dispatch('loginWithEmailCode', { email: this.getEmail, code: this.completeEmailCode })
          .then(() => {
            this.tryFetchingUser();
            // For registrations via invitation code
            if (this.$route.params.team_id && this.$route.params.invitation_code) {
              this.loginProcedure();
            }
          })
          .catch((e) => {
            let message = this.translate('loginCodeError');

            /* NOSONAR
              ErrEmailLoginCodeExpired         = errors2.NewClientError("email verification code has expired")
              ErrEmailLoginCodeTooManyAttempts = errors2.NewClientError("too many attempts - please request a new verification code")
              ErrEmailLoginCodeIncorrect       = errors2.NewClientError("email verification code is incorrect")
             */
            switch (e.response?.data?.message) {
              case 'email verification code has expired':
                message = this.translate('loginCodeErrorExpired');
                break;
              case 'too many attempts - please request a new verification code':
                this.onSendLoginCode();
                message = this.translate('loginCodeErrorRateLimited');
                break;
              case 'email verification code is incorrect':
                message = this.translate('loginCodeError');
                break;
            }

            this.$store.dispatch('setOnboardingErrorMessage', message);
            this.resetInput();
          });
      }
    },

    silentLogin() {
      this.$store.dispatch('loginUsernamePass');
    },

    onSendLoginCode() {
      this.resetInput();

      this.isLoginCodeResent = true;

      setTimeout(() => {
        this.isLoginCodeResent = false;
      }, 1000 * 30);

      if (this.isUpdateEmailPopup && this.newEmail) {
        return this.$store
          .dispatch('sendCodeToNewEmail', {
            email: this.newEmail,
            resend: true,
          })
          .then(() => {
            this.displayUiNotification('loginCodeResent', 'success');
          });
      }

      return this.$store
        .dispatch('sendLoginCodeEmail', { email: this.getEmail })
        .then(() => {
          this.$store.dispatch('setOnboardingErrorMessage', '');
        })
        .catch((error) => {
          this.$store.dispatch(
            'setOnboardingErrorMessage',
            this.translate('loginCodeErrorSending')
          );
          return error;
        });
    },

    loginProcedure() {
      this.$store
        .dispatch('login')
        .then(() => {
          this.$router.push(this.defaultTabName);
          this.$store.dispatch('setLoggedIn');
          if (this.popupMessageNotEmpty) this.$store.dispatch('setPopUpMessage', '');
        })
        .catch((/* error */) => {});
    },

    resetInput() {
      this.$nextTick(() => {
        this.inputs = '';
        this.$refs.codeinput.focus();
      });
    },
  },
};
</script>
